import { useRef } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { trackSearchChanged } from 'metrics/user-analytics/search';
import { decodePassengers, passengerCount } from 'utils/Reserbus';
import parseSearchUrl from 'utils/search/parseSearchUrl';

const DATE_FORMAT_REDUX = 'DD-MM-YYYY';
const DATE_FORMAT_ANALYTICS = 'YYYY-MM-DD';

/**
 * Get the place name from the places object.
 * @param {Object} places - The places object.
 * @param {string} slug - The slug of the place.
 * @returns {string|null} The place name.
 */
function getPlaceName(places, slug) {
  const place = places[slug];
  return place?.cityName || place?.name || null;
}

/**
 * useTrackSearchChanged hook.
 * @param {Object} props - isModalVersion.
 * @param {boolean} props.isModalVersion - Whether the search bar is inside a modal.
 * @returns {Function} trackOnChangeSearch callback.
 */
function useTrackSearchChanged({ isModalVersion }) {
  const {
    origin,
    destination,
    rawDepartureDate,
    rawReturnDate,
    passengers,
    searchId,
    returnSearchId,
    isDepartureOpen,
    isReturnOpen,
  } = useSelector(({ search }) => ({
    origin: search.get('originId'),
    destination: search.get('destinationId'),
    rawDepartureDate: search.get('departureDate'),
    rawReturnDate: search.get('returnDate'),
    passengers: search.get('passengers').toJS(),
    searchId: search.getIn(['departure', 'parentSearchId']),
    returnSearchId: search.getIn(['return', 'parentSearchId']),
    isDepartureOpen: search.getIn(['departure', 'isOpen']) || false,
    isReturnOpen: search.getIn(['return', 'isOpen']) || false,
  }));

  const places = useSelector(({ cities, terminals }) => ({ ...cities, ...terminals }));

  // Ref ensures the callback always reads the latest Redux state, since SearchWidget
  // captures the initial searchCallback reference due to its empty useEffect deps array.
  const latestRef = useRef({});
  latestRef.current = {
    origin,
    destination,
    rawDepartureDate,
    rawReturnDate,
    passengers,
    searchId,
    returnSearchId,
    isDepartureOpen,
    isReturnOpen,
    places,
  };

  /**
   * Tracks the search changed event.
   * @param {string} absoluteSearchUrl - The new search URL submitted by the widget.
   */
  const trackOnChangeSearch = (absoluteSearchUrl) => {
    const {
      rawDepartureDate: beforeRawDeparture,
      rawReturnDate: beforeRawReturn,
      origin: beforeOriginSlug,
      destination: beforeDestinationSlug,
      passengers: beforePassengers,
      searchId: currentSearchId,
      returnSearchId: currentReturnSearchId,
      isDepartureOpen: beforeDepartureOpen,
      isReturnOpen: beforeReturnOpen,
      places: currentPlaces,
    } = latestRef.current;

    // Use 'open' as a sentinel for open-ticket dates so null stays unambiguous ("no date").
    /**
     * Convert the raw date to the date format for analytics.
     * @param {string} rawDate - The raw date string.
     * @param {boolean} isOpen - Whether the date is open.
     * @returns {string} - The date value.
     */
    const toDateValue = (rawDate, isOpen) => {
      if (isOpen) return 'open';
      return rawDate ? moment(rawDate, DATE_FORMAT_REDUX).format(DATE_FORMAT_ANALYTICS) : null;
    };

    const beforeDeparture = toDateValue(beforeRawDeparture, beforeDepartureOpen);
    const beforeReturn = toDateValue(beforeRawReturn, beforeReturnOpen);

    let afterOriginSlug = beforeOriginSlug;
    let afterDestinationSlug = beforeDestinationSlug;
    let afterDeparture = beforeDeparture;
    let afterReturn = beforeReturn;
    let afterPassengers = passengerCount(beforePassengers);

    if (absoluteSearchUrl) {
      try {
        const { pathname, search: queryString } = new URL(absoluteSearchUrl);
        const parsed = parseSearchUrl(pathname, queryString);
        if (parsed) {
          afterOriginSlug = parsed.originSlug;
          afterDestinationSlug = parsed.destinationSlug;
          afterDeparture = parsed.isDepartureOpen ? 'open' : parsed.departureDate;
          afterReturn = parsed.isReturnOpen ? 'open' : parsed.returnDate;
          if (parsed.passengersStr) {
            afterPassengers = passengerCount(decodePassengers(parsed.passengersStr));
          }
        }
      } catch (_) {
        // Gracefully fall back to before-state values if URL parsing fails
      }
    }

    const changedFields = [];
    const inputsChanged = {};

    if (beforeDeparture !== afterDeparture) {
      changedFields.push('departure');
      inputsChanged.departure = { before: beforeDeparture, after: afterDeparture };
    }
    if (beforeReturn !== afterReturn) {
      changedFields.push('return');
      inputsChanged.return = { before: beforeReturn, after: afterReturn };
    }
    if (beforeOriginSlug !== afterOriginSlug) {
      changedFields.push('origin');
      inputsChanged.origin = { before: beforeOriginSlug, after: afterOriginSlug };
    }
    if (beforeDestinationSlug !== afterDestinationSlug) {
      changedFields.push('destination');
      inputsChanged.destination = { before: beforeDestinationSlug, after: afterDestinationSlug };
    }

    const beforeHasReturn = beforeReturn !== null;
    const afterHasReturn = afterReturn !== null;

    trackSearchChanged({
      searchType: isModalVersion ? 'modal' : 'header',
      origin: getPlaceName(currentPlaces, afterOriginSlug),
      destination: getPlaceName(currentPlaces, afterDestinationSlug),
      departure: afterDeparture,
      returnDate: afterReturn,
      passengers: afterPassengers,
      changedFields,
      inputsChanged,
      tripTypeBefore: beforeHasReturn ? 'round-trip' : 'one-way',
      tripTypeAfter: afterHasReturn ? 'round-trip' : 'one-way',
      addedReturn: !beforeHasReturn && afterHasReturn,
      removedReturn: beforeHasReturn && !afterHasReturn,
      searchId: currentSearchId,
      returnSearchId: currentReturnSearchId,
    });
  };

  return trackOnChangeSearch;
}

export default useTrackSearchChanged;
